/**
 * CyberSells Post-Purchase Rewards Module
 * Processes and displays rewards after a purchase is complete
 */

(function() {
  'use strict';

  /**
   * Process purchase and calculate rewards
   */
  function processPurchaseRewards(purchaseData) {
    if (!window.CyberSells_playerData || !window.CyberSells_stats) {
      if (window.cyberSellsConfig?.debug) {
        console.warn('[CyberSells] Required modules not loaded for post-purchase');
      }
      return null;
    }

    // Load player data
    const playerData = window.CyberSells_playerData.loadPlayerData();
    
    // Calculate XP from purchase
    const xpGained = window.CyberSells_stats.calculatePurchaseXp(purchaseData.totalPrice);
    const levelResult = window.CyberSells_stats.awardXp(playerData, xpGained);
    
    // Apply stat changes from purchased items
    const statDeltas = calculateItemStats(purchaseData.items);
    window.CyberSells_playerData.updatePlayerStats(playerData, statDeltas);
    
    // Update purchase tracking
    playerData.purchaseCount = (playerData.purchaseCount || 0) + 1;
    playerData.totalSpent = (playerData.totalSpent || 0) + purchaseData.totalPrice;
    
    // Track purchased items
    if (Array.isArray(purchaseData.items)) {
      if (!playerData.itemsPurchased) {
        playerData.itemsPurchased = [];
      }
      
      purchaseData.items.forEach(item => {
        playerData.itemsPurchased.push({
          id: item.id || item.variant_id,
          title: item.title || item.product_title,
          stats: item.stats || statDeltas,
          purchasedAt: Date.now(),
          orderId: purchaseData.orderId
        });
      });
    }
    
    // Check for new achievements
    let newAchievements = [];
    let achievementXp = 0;
    
    if (window.CyberSells_achievements) {
      newAchievements = window.CyberSells_achievements.checkAchievements(playerData);
      achievementXp = window.CyberSells_achievements.awardAchievements(playerData, newAchievements);
      
      // Award achievement XP (may cause additional level up)
      if (achievementXp > 0) {
        const achievementLevelResult = window.CyberSells_stats.awardXp(playerData, achievementXp);
        if (achievementLevelResult.leveledUp) {
          levelResult.leveledUp = true;
          levelResult.newLevel = achievementLevelResult.newLevel;
        }
      }
    }
    
    // Track quest progress
    let completedQuests = [];
    if (window.CyberSells_quests) {
      completedQuests = window.CyberSells_quests.trackQuestEvent('purchase', purchaseData, playerData);
      
      // Also track purchase value for spending quests
      window.CyberSells_quests.trackQuestEvent('purchase_value', purchaseData, playerData);
      
      // Track achievement unlock for quests
      if (newAchievements.length > 0) {
        const moreQuests = window.CyberSells_quests.trackQuestEvent('achievement', {}, playerData);
        completedQuests = [...completedQuests, ...moreQuests];
      }
    }
    
    // Save player data
    window.CyberSells_playerData.savePlayerData(playerData);
    
    // Calculate next milestones
    const nextMilestones = calculateNextMilestones(playerData);
    
    // Prepare reward summary
    const rewards = {
      xpGained,
      totalXpGained: xpGained + achievementXp,
      levelResult,
      statDeltas,
      newAchievements: newAchievements.map(id => window.CyberSells_achievements?.getAchievementById(id)).filter(Boolean),
      completedQuests,
      playerData,
      nextMilestones,
      purchaseData
    };
    
    // Dispatch event
    document.dispatchEvent(new CustomEvent('cybersells:post-purchase', {
      detail: rewards
    }));
    
    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Post-purchase rewards:', rewards);
    }
    
    return rewards;
  }

  /**
   * Calculate total stat changes from items
   */
  function calculateItemStats(items) {
    const statDeltas = {};
    
    if (!Array.isArray(items)) return statDeltas;
    
    items.forEach(item => {
      const stats = item.stats || {};
      for (const [stat, value] of Object.entries(stats)) {
        statDeltas[stat] = (statDeltas[stat] || 0) + (value * (item.quantity || 1));
      }
    });
    
    return statDeltas;
  }

  /**
   * Calculate next milestones for player
   */
  function calculateNextMilestones(playerData) {
    const milestones = [];
    
    // Next level milestone
    if (window.CyberSells_stats) {
      const nextLevelXp = window.CyberSells_stats.getXpForLevel(playerData.level);
      const xpNeeded = nextLevelXp - playerData.xp;
      
      if (xpNeeded > 0) {
        milestones.push({
          type: 'level',
          title: `Reach Level ${playerData.level + 1}`,
          icon: '?',
          progress: playerData.xp,
          target: nextLevelXp,
          remaining: xpNeeded,
          percentage: Math.floor((playerData.xp / nextLevelXp) * 100)
        });
      }
    }
    
    // Achievement milestones
    if (window.CyberSells_achievements) {
      const locked = window.CyberSells_achievements.getLockedAchievements(playerData);
      
      // Find closest achievements (simplified - would need better logic)
      const closeAchievements = locked.slice(0, 2);
      closeAchievements.forEach(achievement => {
        milestones.push({
          type: 'achievement',
          title: achievement.title,
          description: achievement.description,
          icon: achievement.icon,
          reward: `+${achievement.xpReward} XP`
        });
      });
    }
    
    // Purchase count milestones
    const purchaseTargets = [5, 10, 25, 50];
    const nextPurchaseTarget = purchaseTargets.find(t => t > (playerData.purchaseCount || 0));
    if (nextPurchaseTarget) {
      milestones.push({
        type: 'purchases',
        title: `Make ${nextPurchaseTarget} Purchases`,
        icon: '???',
        progress: playerData.purchaseCount || 0,
        target: nextPurchaseTarget,
        remaining: nextPurchaseTarget - (playerData.purchaseCount || 0)
      });
    }
    
    return milestones;
  }

  /**
   * Display post-purchase rewards
   */
  function displayPostPurchaseRewards(rewards) {
    const container = document.querySelector('[data-rewards-container]');
    if (!container) return;

    container.innerHTML = renderRewards(rewards);
    
    // Show social sharing if major milestone
    if (rewards.levelResult.leveledUp || rewards.newAchievements.length > 0) {
      const shareContainer = document.querySelector('[data-share-container]');
      if (shareContainer) {
        shareContainer.style.display = 'block';
        setupSocialSharing(rewards);
      }
    }
    
    // Show next steps
    const nextSteps = document.querySelector('[data-next-steps]');
    if (nextSteps && rewards.nextMilestones.length > 0) {
      nextSteps.style.display = 'block';
      displayNextSteps(rewards.nextMilestones);
    }
  }

  /**
   * Render rewards HTML
   */
  function renderRewards(rewards) {
    let html = '';
    
    // XP Reward Card
    html += '<div class="cs-reward-card">';
    html += '<div class="cs-reward-card__header">';
    html += '<div class="cs-reward-card__icon">?</div>';
    html += '<h3 class="cs-reward-card__title">Rewards Earned!</h3>';
    html += '</div>';
    html += '<div class="cs-reward-card__body">';
    html += '<div class="cs-reward-card__stats">';
    html += `<div class="cs-reward-stat">`;
    html += `<div class="cs-reward-stat__label">XP Gained</div>`;
    html += `<div class="cs-reward-stat__value">${rewards.totalXpGained.toLocaleString()}</div>`;
    html += `</div>`;
    html += `<div class="cs-reward-stat">`;
    html += `<div class="cs-reward-stat__label">Current Level</div>`;
    html += `<div class="cs-reward-stat__value">${rewards.levelResult.newLevel}</div>`;
    html += `</div>`;
    html += '</div>';
    html += '</div>';
    html += '</div>';
    
    // Level Up Card
    if (rewards.levelResult.leveledUp) {
      html += '<div class="cs-reward-card cs-reward-card--level-up">';
      html += '<div class="cs-reward-card__header">';
      html += '<div class="cs-reward-card__icon">??</div>';
      html += '<h3 class="cs-reward-card__title">LEVEL UP!</h3>';
      html += '</div>';
      html += '<div class="cs-reward-card__body">';
      html += `<p>Congratulations! You've reached <strong>Level ${rewards.levelResult.newLevel}</strong>!</p>`;
      html += '</div>';
      html += '</div>';
    }
    
    // Stat Changes
    if (Object.keys(rewards.statDeltas).length > 0) {
      html += '<div class="cs-reward-card">';
      html += '<div class="cs-reward-card__header">';
      html += '<div class="cs-reward-card__icon">??</div>';
      html += '<h3 class="cs-reward-card__title">Stat Boosts</h3>';
      html += '</div>';
      html += '<div class="cs-reward-card__body">';
      html += '<div class="cs-reward-card__stats">';
      
      for (const [stat, delta] of Object.entries(rewards.statDeltas)) {
        if (delta > 0) {
          html += `<div class="cs-reward-stat">`;
          html += `<div class="cs-reward-stat__label">${stat.toUpperCase()}</div>`;
          html += `<div class="cs-reward-stat__value">+${delta}</div>`;
          html += `</div>`;
        }
      }
      
      html += '</div>';
      html += '</div>';
      html += '</div>';
    }
    
    // Achievements
    if (rewards.newAchievements.length > 0) {
      rewards.newAchievements.forEach(achievement => {
        html += '<div class="cs-reward-card">';
        html += '<div class="cs-reward-card__header">';
        html += `<div class="cs-reward-card__icon">${achievement.icon}</div>`;
        html += '<h3 class="cs-reward-card__title">Achievement Unlocked!</h3>';
        html += '</div>';
        html += '<div class="cs-reward-card__body">';
        html += `<h4>${achievement.title}</h4>`;
        html += `<p>${achievement.description}</p>`;
        html += `<p class="cs-reward-card__xp">+${achievement.xpReward} XP</p>`;
        html += '</div>';
        html += '</div>';
      });
    }
    
    // Completed Quests
    if (rewards.completedQuests.length > 0) {
      rewards.completedQuests.forEach(quest => {
        html += '<div class="cs-reward-card">';
        html += '<div class="cs-reward-card__header">';
        html += `<div class="cs-reward-card__icon">${quest.icon}</div>`;
        html += '<h3 class="cs-reward-card__title">Quest Complete!</h3>';
        html += '</div>';
        html += '<div class="cs-reward-card__body">';
        html += `<h4>${quest.title}</h4>`;
        html += `<p>${quest.description}</p>`;
        if (quest.reward.xp) {
          html += `<p class="cs-reward-card__xp">+${quest.reward.xp} XP</p>`;
        }
        html += '</div>';
        html += '</div>';
      });
    }
    
    return html;
  }

  /**
   * Display next milestones
   */
  function displayNextSteps(milestones) {
    const container = document.querySelector('[data-milestones]');
    if (!container) return;

    let html = '';
    milestones.forEach(milestone => {
      html += '<div class="cs-milestone">';
      html += `<div class="cs-milestone__icon">${milestone.icon}</div>`;
      html += '<div class="cs-milestone__info">';
      html += `<h4 class="cs-milestone__title">${milestone.title}</h4>`;
      if (milestone.description) {
        html += `<p class="cs-milestone__description">${milestone.description}</p>`;
      }
      if (milestone.progress !== undefined && milestone.target !== undefined) {
        const percentage = Math.floor((milestone.progress / milestone.target) * 100);
        html += '<div class="cs-milestone__progress">';
        html += `<div class="cs-milestone__progress-bar"><div class="cs-milestone__progress-fill" style="width: ${percentage}%"></div></div>`;
        html += `<div class="cs-milestone__progress-text">${milestone.progress} / ${milestone.target}</div>`;
        html += '</div>';
      }
      html += '</div>';
      html += '</div>';
    });

    container.innerHTML = html;
  }

  /**
   * Setup social sharing
   */
  function setupSocialSharing(rewards) {
    const twitterBtn = document.querySelector('[data-share="twitter"]');
    const facebookBtn = document.querySelector('[data-share="facebook"]');
    
    const shareText = generateShareText(rewards);
    const shareUrl = window.location.href;
    
    if (twitterBtn) {
      twitterBtn.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    }
    
    if (facebookBtn) {
      facebookBtn.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    }
  }

  /**
   * Generate share text
   */
  function generateShareText(rewards) {
    if (rewards.levelResult.leveledUp) {
      return `I just reached Level ${rewards.levelResult.newLevel}! ??`;
    }
    if (rewards.newAchievements.length > 0) {
      const achievement = rewards.newAchievements[0];
      return `I unlocked the "${achievement.title}" achievement! ${achievement.icon}`;
    }
    return `I just earned ${rewards.totalXpGained} XP! ?`;
  }

  // Expose module functions
  window.CyberSells_postPurchase = {
    processPurchaseRewards,
    displayPostPurchaseRewards
  };

  // Auto-initialize on post-purchase pages
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPostPurchase);
  } else {
    initPostPurchase();
  }

  function initPostPurchase() {
    const postPurchaseEl = document.querySelector('[data-cs-post-purchase]');
    if (!postPurchaseEl) return;

    // Check for order data (Shopify thank you page)
    if (window.Shopify && window.Shopify.checkout) {
      const checkout = window.Shopify.checkout;
      
      const purchaseData = {
        orderId: checkout.order_id,
        totalPrice: Math.round(checkout.total_price * 100), // Convert to cents
        items: (checkout.line_items || []).map(item => ({
          id: item.variant_id,
          title: item.title,
          product_title: item.product_title,
          quantity: item.quantity,
          price: Math.round(item.price * 100),
          stats: {} // Would need to be added via metafields
        }))
      };

      const rewards = processPurchaseRewards(purchaseData);
      if (rewards) {
        displayPostPurchaseRewards(rewards);
      }
    }
  }

})();
